import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Phone, ArrowRight, Snowflake, Flame, Wrench, Building2, Siren, Wind,
  ShieldCheck, Clock, MapPin, BadgeCheck,
} from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import GHLForm from "@/components/GHLForm";
import FAQ from "@/components/FAQ";
import CTABand from "@/components/CTABand";
import { homeFaqs } from "@/data/faqs";
import SEO from "@/components/SEO";
import { businessSchema, faqSchema, servicesSchema } from "@/data/schema";

const trustBadges = [
  { icon: Clock, title: "24/7 Availability", text: "Live emergency dispatch — nights, weekends, and holidays included." },
  { icon: BadgeCheck, title: "Certified Technicians", text: "Licensed, insured mechanical HVAC professionals on every truck." },
  { icon: Building2, title: "Residential & Commercial", text: "From single-family homes to rooftop package units and facilities." },
  { icon: MapPin, title: "Fast Local Dispatch", text: "Based in Trout, LA — centrally located for rapid response parish-wide." },
];

const services = [
  {
    icon: Snowflake, accent: "frost", title: "AC Repair & Cooling",
    text: "Diagnostics and same-visit repair for central air conditioners, heat pumps, and ductless mini-splits. When Louisiana humidity peaks, we restore cold air fast.",
  },
  {
    icon: Flame, accent: "ember", title: "Heating & Furnace Service",
    text: "Gas and electric furnace repair, heat pump heating service, and full safety inspections so your family or facility stays warm through every cold snap.",
  },
  {
    icon: Wrench, accent: "frost", title: "Full System Installation",
    text: "Load-calculated sizing, code-compliant installation, and manufacturer-registered warranties on high-efficiency replacement systems for any budget.",
  },
  {
    icon: Building2, accent: "ember", title: "Commercial Mechanical HVAC",
    text: "Rooftop package units, commercial split systems, and ventilation for offices, retail, churches, and industrial spaces across Central Louisiana.",
  },
  {
    icon: Siren, accent: "ember", title: "24/7 Emergency Repairs",
    text: "A live answer at any hour and a stocked truck on the road. Emergency HVAC repair is not an add-on for us — it is the core of how we operate.",
  },
  {
    icon: Wind, accent: "frost", title: "Indoor Air Quality",
    text: "Whole-home dehumidification, high-efficiency filtration, UV germicidal lights, and duct sealing built for Louisiana's humid climate.",
  },
];

const towns = [
  { name: "Trout", top: "46%", left: "48%", home: true },
  { name: "Jena", top: "62%", left: "40%" },
  { name: "Urania", top: "55%", left: "30%" },
  { name: "Olla", top: "38%", left: "36%" },
  { name: "Alexandria", top: "58%", left: "68%" },
  { name: "Pineville", top: "50%", left: "74%" },
  { name: "Pollock", top: "74%", left: "58%" },
  { name: "Dry Prong", top: "70%", left: "44%" },
  { name: "Winnfield", top: "22%", left: "42%" },
  { name: "Georgetown", top: "80%", left: "50%" },
];

const marqueeItems = [
  "24/7 Emergency Dispatch", "(318) 312-2112", "Trout LA", "Jena", "Alexandria", "Pineville",
  "Residential & Commercial", "Heating", "Cooling", "Installation", "LaSalle Parish", "Central Louisiana",
];

const fieldPhotos = [
  {
    src: "https://images.unsplash.com/photo-1698479603408-1a66a6d9e80f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxodmFjJTIwdGVjaG5pY2lhbiUyMHJlcGFpciUyMGFpciUyMGNvbmRpdGlvbmVyJTIwaGVhdGluZyUyMGNvb2xpbmd8ZW58MHx8fHwxNzg4NDU1NjcyfDA&ixlib=rb-4.1.0&q=85",
    alt: "Technician performing professional HVAC repair on an outdoor unit",
    caption: "Field Diagnostics & Repair",
  },
  {
    src: "https://images.unsplash.com/photo-1700124113583-81aa99ea2aa2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhaXIlMjBjb25kaXRpb25lciUyMGhlYXQlMjBwdW1wJTIwaG9tZSUyMHN5c3RlbXxlbnwwfHx8fDE3ODg0NTU2NzJ8MA&ixlib=rb-4.1.0&q=85",
    alt: "Modern outdoor heat pump condenser unit installed at a Louisiana home",
    caption: "Heat Pump Installations",
  },
  {
    src: "https://images.unsplash.com/photo-1667983453881-4992fe86ab1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhaXIlMjBjb25kaXRpb25lciUyMGhlYXQlMjBwdW1wJTIwaG9tZSUyMHN5c3RlbXxlbnwwfHx8fDE3ODg0NTU2NzJ8MA&ixlib=rb-4.1.0&q=85",
    alt: "Commercial rooftop HVAC mechanical system serviced by certified technicians",
    caption: "Commercial Mechanical Systems",
  },
  {
    src: "https://images.unsplash.com/photo-1762341123870-d706f257a12e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBhaXIlMjBjb25kaXRpb25lciUyMGhlYXQlMjBwdW1wJTIwaG9tZSUyMHN5c3RlbXxlbnwwfHx8fDE3ODg0NTU2NzJ8MA&ixlib=rb-4.1.0&q=85",
    alt: "Indoor air conditioning air handler unit maintained by McCann and Sons",
    caption: "Air Handlers & Seasonal Maintenance",
  },
];

export default function Home() {
  const { scrollY } = useScroll();
  const heroImgY = useTransform(scrollY, [0, 700], [0, 140]);
  const heroTextY = useTransform(scrollY, [0, 700], [0, -60]);

  return (
    <div data-testid="home-page">
      <SEO
        title="24/7 HVAC Repair Trout LA | McCann & Sons HVAC"
        description="24/7 emergency HVAC repair, AC installation & commercial mechanical service in Trout & Central Louisiana. Family-owned. Call (318) 312-2112."
        path="/"
        jsonld={[faqSchema(homeFaqs), ...servicesSchema(window.location.origin)]}
      />
      {/* HERO */}
      <section className="relative bg-navy-deep overflow-hidden grain-overlay">
        <motion.div style={{ y: heroImgY }} className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1698479603408-1a66a6d9e80f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxodmFjJTIwdGVjaG5pY2lhbiUyMHJlcGFpciUyMGFpciUyMGNvbmRpdGlvbmVyJTIwaGVhdGluZyUyMGNvb2xpbmd8ZW58MHx8fHwxNzg4NDU1NjcyfDA&ixlib=rb-4.1.0&q=85"
            alt="HVAC technician servicing a commercial air conditioning unit in Central Louisiana"
            className="w-full h-full object-cover opacity-25 scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/70 via-navy-deep/85 to-navy-deep" />
          <div className="absolute inset-0 hero-grid-bg" />
        </motion.div>

        <motion.div style={{ y: heroTextY }} className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-20 pb-24 lg:pt-32 lg:pb-36">
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2.5 border border-ember/50 bg-ember/10 text-ember font-mono text-xs uppercase tracking-[0.22em] px-4 py-2.5 rounded-sm mb-8"
            data-testid="hero-emergency-badge"
          >
            <span className="h-2 w-2 rounded-full bg-ember animate-pulse-dot" />
            24/7 Emergency Dispatch — Active Now
          </motion.div>

          <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl font-black uppercase tracking-tight leading-[0.92] text-white">
            <MaskedLine delay={0.15}>Central Louisiana's</MaskedLine>
            <MaskedLine delay={0.3}>
              <span className="text-ember">24/7 Heating</span> &
            </MaskedLine>
            <MaskedLine delay={0.45}>
              <span className="text-frost">Cooling</span> Experts
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-7 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed"
          >
            McCann and Sons HVAC Service is a family-owned mechanical contractor headquartered in Trout,
            Louisiana. We repair, maintain, and install heating and cooling systems for homes and businesses
            across Central Louisiana — with honest, upfront pricing and a certified technician on call
            around the clock, every day of the year.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              data-testid="hero-emergency-call-btn"
              href="tel:3183122112"
              className="inline-flex items-center justify-center gap-3 bg-ember hover:bg-ember-dark text-navy-deep font-bold text-base px-8 py-5 rounded-sm transition-all hover:-translate-y-1 active:scale-95 shadow-[0_10px_40px_rgba(238,125,74,0.35)]"
            >
              <Siren className="h-5 w-5" />
              Emergency Repair — (318) 312-2112
            </a>
            <Link
              data-testid="hero-estimate-btn"
              to="/contact"
              className="inline-flex items-center justify-center gap-3 border border-frost/60 text-frost hover:bg-frost hover:text-navy-deep font-bold text-base px-8 py-5 rounded-sm transition-all hover:-translate-y-1 active:scale-95"
            >
              Request an Estimate
              <ArrowRight className="h-5 w-5" />
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* TRUST BADGES */}
      <section className="bg-navy border-y border-white/10" data-testid="trust-badges-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {trustBadges.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.08} className={`py-8 lg:py-10 px-2 lg:px-8 ${i > 0 ? "lg:border-l border-white/10" : ""} border-t sm:border-t-0 first:border-t-0`}>
              <b.icon className={`h-6 w-6 mb-4 ${i % 2 === 0 ? "text-ember" : "text-frost"}`} />
              <h3 className="font-display text-xl font-bold uppercase tracking-wide text-white">{b.title}</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{b.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <Marquee items={marqueeItems} dark />

      {/* SERVICES GRID */}
      <section className="bg-slate-50 py-20 lg:py-32" data-testid="services-grid-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-8 mb-14">
            <Reveal className="lg:col-span-7">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember mb-4">What We Do</p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[0.95] text-navy">
                Mechanical HVAC Service, Repair & Installation
              </h2>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-5 flex items-end">
              <p className="text-slate-600 leading-relaxed">
                From a midnight compressor failure in Jena to a full rooftop unit replacement in Alexandria,
                our trucks roll with the parts, tools, and certified mechanical expertise to finish the job
                in one visit. Cooling work is backed by the same discipline as our heating and commercial
                mechanical projects — measured, tested, and guaranteed.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <Reveal key={s.title} delay={(i % 3) * 0.1}>
                <Link
                  to="/services"
                  data-testid={`service-card-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`group block h-full bg-white border-t-4 ${
                    s.accent === "ember" ? "border-t-ember" : "border-t-frost"
                  } border-x border-b border-slate-200 p-7 rounded-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgba(15,9,71,0.12)]`}
                >
                  <s.icon className={`h-8 w-8 mb-5 ${s.accent === "ember" ? "text-ember" : "text-frost"} transition-transform group-hover:scale-110`} />
                  <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-navy">{s.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.text}</p>
                  <span className={`mt-5 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] ${
                    s.accent === "ember" ? "text-ember" : "text-frost-dark"
                  }`}>
                    Explore <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FIELD GALLERY */}
      <section className="bg-white py-20 lg:py-28 border-t border-slate-100" data-testid="field-gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-frost-dark mb-4">In The Field</p>
              <h2 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.95] text-navy">
                Real Systems. Real Mechanical Work.
              </h2>
            </div>
            <p className="max-w-md text-slate-600 leading-relaxed">
              From residential condensers and heat pumps to commercial rooftop mechanical units — this is
              the equipment our certified technicians service across Central Louisiana every day.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {fieldPhotos.map((p, i) => (
              <Reveal key={p.caption} delay={i * 0.08}>
                <figure className="group relative overflow-hidden rounded-sm border border-slate-200 aspect-[3/4] bg-navy-deep">
                  <img
                    src={p.src}
                    alt={p.alt}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-deep/90 via-navy-deep/10 to-transparent" />
                  <figcaption className="absolute bottom-0 left-0 right-0 p-5">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ember">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="font-display text-lg font-bold uppercase tracking-wide text-white leading-tight mt-1">
                      {p.caption}
                    </p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="bg-navy-deep py-20 lg:py-32 relative overflow-hidden grain-overlay" data-testid="why-choose-section">
        <div className="hero-grid-bg absolute inset-0" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <Reveal className="max-w-3xl mb-16">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-frost mb-4">Why McCann & Sons</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[0.95] text-white">
              Built On Reliability. Kept By Word Of Mouth.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
            {[
              {
                n: "01", title: "Local To The Core",
                text: "We live where we work. Our shop sits on Dixie Lane in Trout, and our technicians know the back roads of LaSalle, Grant, Rapides, Winn, and Catahoula parishes by heart. When you call, you are not routed to a call center three states away — you reach neighbors who can be at your door fast.",
              },
              {
                n: "02", title: "Upfront, Honest Pricing",
                text: "Every repair begins with a diagnostic and a flat-rate quote you approve before work starts. No hourly meters running, no mystery line items, no pressure to replace equipment that still has honest years left in it. That is how a family business earns a second generation of customers.",
              },
              {
                n: "03", title: "Licensed Mechanical Expertise",
                text: "HVAC is mechanical work — refrigerant circuits, combustion safety, electrical controls, airflow science. Our certified technicians are trained on residential systems and heavy-duty commercial mechanical equipment alike, and every install is permitted, inspected, and warranty-registered.",
              },
              {
                n: "04", title: "A True 24-Hour Operation",
                text: "Anyone can print '24/7' on a truck. We staff it. Nights, weekends, and holidays, a real technician answers and a stocked vehicle rolls. Central Louisiana weather does not wait for Monday morning, and neither do we.",
              },
            ].map((item, i) => (
              <Reveal key={item.n} delay={i * 0.08} className="bg-navy-deep p-8 lg:p-12">
                <span className="font-mono text-sm text-ember">{item.n}</span>
                <h3 className="mt-3 font-display text-2xl lg:text-3xl font-bold uppercase tracking-wide text-white">{item.title}</h3>
                <p className="mt-4 text-sm text-slate-400 leading-relaxed">{item.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FORM SECTION */}
      <section className="bg-white py-20 lg:py-32" data-testid="dispatch-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember mb-4">Fast Dispatch Inquiry</p>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.95] text-navy">
              Request Service Or A Free Estimate
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Tell us what your system is doing — or not doing — and our dispatcher will respond promptly
              with scheduling options. For planned work like system replacement, installation, or a
              maintenance plan, this form is the fastest route to a written, no-obligation quote.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Facing an emergency right now? Skip the form. Call
              <a href="tel:3183122112" className="text-ember font-semibold hover:underline"> (318) 312-2112 </a>
              and you will reach our 24-hour line immediately — a live person, not a voicemail box.
            </p>
            <div className="mt-8 flex items-start gap-4 border-l-4 border-ember bg-slate-50 p-5 rounded-sm">
              <ShieldCheck className="h-6 w-6 text-ember shrink-0 mt-0.5" />
              <p className="text-sm text-slate-600 leading-relaxed">
                Every inquiry is answered by McCann and Sons directly — your information is never sold or
                shared with third-party lead services.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <GHLForm />
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-50 py-20 lg:py-32" data-testid="home-faq-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-ember mb-4">Common Questions</p>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.95] text-navy">
              HVAC Answers, Straight Up
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              The questions Central Louisiana homeowners and facility managers ask us most — answered the
              same way we would across your kitchen table.
            </p>
          </Reveal>
          <div className="lg:col-span-8">
            <FAQ items={homeFaqs} />
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="bg-white py-20 lg:py-32" data-testid="service-area-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-frost-dark mb-4">Service Area</p>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight leading-[0.95] text-navy">
              Proudly Based In Trout. Serving All Of Central Louisiana.
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Our home base at 164 Dixie Lane in Trout puts us within quick reach of communities across
              LaSalle Parish and the greater Central Louisiana region. We dispatch daily to Jena, Urania,
              Olla, and Tullos; run regular routes into Alexandria, Pineville, and Ball; and cover Pollock,
              Dry Prong, Georgetown, Winnfield, and the rural stretches in between.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              If your home or business sits outside these towns, call anyway — for emergency repairs and
              commercial mechanical projects, our service radius extends throughout the surrounding
              communities. If we can reach you, we will take care of you.
            </p>
            <div className="mt-8 flex flex-wrap gap-2">
              {towns.map((t) => (
                <span
                  key={t.name}
                  className={`font-mono text-xs uppercase tracking-[0.15em] px-3 py-1.5 rounded-sm border ${
                    t.home
                      ? "bg-ember text-navy-deep border-ember font-bold"
                      : "border-slate-300 text-slate-600"
                  }`}
                >
                  {t.name}{t.home ? " — HQ" : ""}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div
              data-testid="service-area-map"
              className="relative aspect-[4/3] rounded-md overflow-hidden border border-slate-200 shadow-[0_20px_60px_rgba(15,9,71,0.15)]"
            >
              <iframe
                title="McCann and Sons HVAC Service area map — Trout, Louisiana 71371"
                src="https://maps.google.com/maps?q=Trout%2C%20Louisiana%2071371&t=&z=9&ie=UTF8&iwloc=&output=embed"
                className="absolute inset-0 w-full h-full"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
              Live Map — Trout, LA 71371 · Central Louisiana Coverage
            </p>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
